import * as React from 'react';
import { Badge, Button, Checkbox, DatePicker, Empty, Input, Popconfirm, Popover, Segmented, Select, Spin, Typography, notification } from 'antd';
import { CalendarOutlined, CommentOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import axiosApiInstance from '../../../../api/axiosClient';
import { userAtom } from '../../../../context/auth_atoms';
import { useCampaignTasks, useCreateCampaignTask, useDeleteCampaignTask, useUpdateCampaignTask, type AssigneeOption, type TaskPatch } from '../queryHooks';
import type { CampaignTask } from '../types';

function assigneeKey(person: { kind: string; id?: number; uid?: string }): string {
    return person.kind === 'user' && person.uid ? `user:${person.uid}` : `employee:${person.id}`;
}

function taskAssigneeKey(task: CampaignTask): string | undefined {
    if (task.assignee_uid) return `user:${task.assignee_uid}`;
    if (task.assignee_id) return `employee:${task.assignee_id}`;
    return undefined;
}

function splitAssignee(key?: string): { assigneeId?: number; assigneeUid?: string } {
    if (!key) return {};
    if (key.startsWith('user:')) return { assigneeUid: key.slice('user:'.length) };
    const id = Number(key.slice('employee:'.length));
    return Number.isFinite(id) ? { assigneeId: id } : {};
}

function groupTasks(tasks: CampaignTask[]): { label: string; tasks: CampaignTask[] }[] {
    const today = dayjs().startOf('day');
    const overdue: CampaignTask[] = [];
    const dueToday: CampaignTask[] = [];
    const upcoming = new Map<string, CampaignTask[]>();
    const none: CampaignTask[] = [];
    for (const task of tasks) {
        if (!task.due_date) {
            none.push(task);
            continue;
        }
        const due = dayjs(task.due_date).startOf('day');
        if (due.isBefore(today)) overdue.push(task);
        else if (due.isSame(today)) dueToday.push(task);
        else {
            const key = due.format('YYYY-MM-DD');
            upcoming.set(key, [...(upcoming.get(key) ?? []), task]);
        }
    }
    const groups: { label: string; tasks: CampaignTask[] }[] = [];
    if (overdue.length) groups.push({ label: 'Overdue', tasks: overdue });
    if (dueToday.length) groups.push({ label: 'Today', tasks: dueToday });
    [...upcoming.keys()].sort().forEach((key) => {
        groups.push({ label: dayjs(key).format('D MMM YYYY'), tasks: upcoming.get(key) ?? [] });
    });
    if (none.length) groups.push({ label: 'No Due Date', tasks: none });
    return groups;
}

const AssigneeSelect: React.FC<{
    people: AssigneeOption[];
    value?: string;
    onChange: (key?: string) => void;
}> = ({ people, value, onChange }) => (
    <Select
        showSearch
        allowClear
        placeholder="Assignee"
        value={value}
        style={{ width: 180 }}
        listHeight={240}
        optionFilterProp="label"
        options={people.map((person) => ({
            value: assigneeKey(person),
            label: person.kind === 'user' ? `${person.name} (dev)` : person.name,
        }))}
        onChange={(key) => onChange(typeof key === 'string' ? key : undefined)}
    />
);

const TaskRow: React.FC<{
    task: CampaignTask;
    people: AssigneeOption[];
    busy: boolean;
    onPatch: (patch: TaskPatch) => void;
    onDelete: () => void;
}> = ({ task, people, busy, onPatch, onDelete }) => {
    const [title, setTitle] = React.useState(task.title);
    const [note, setNote] = React.useState(task.note ?? '');
    React.useEffect(() => setTitle(task.title), [task.title]);
    React.useEffect(() => setNote(task.note ?? ''), [task.note]);

    const saveTitle = () => {
        const next = title.trim();
        if (!next || next === task.title) {
            setTitle(task.title);
            return;
        }
        onPatch({ title: next });
    };

    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            border: '1px solid #d6e4ff', borderRadius: 6, padding: '6px 10px', marginBottom: 8,
            background: 'var(--surface)', opacity: task.done ? 0.55 : 1,
        }}>
            <Checkbox checked={task.done} disabled={busy} onChange={(e) => onPatch({ done: e.target.checked })} />
            <Input
                bordered={false}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={saveTitle}
                onPressEnter={saveTitle}
                style={{ flex: 1, textDecoration: task.done ? 'line-through' : undefined }}
            />
            <DatePicker
                value={task.due_date ? dayjs(task.due_date) : null}
                format="D MMM"
                allowClear
                placeholder=""
                suffixIcon={<CalendarOutlined />}
                style={{ width: task.due_date ? 110 : 36 }}
                onChange={(d) => onPatch(d ? { due_date: d.format('YYYY-MM-DD') } : { clear_due: true })}
            />
            <AssigneeSelect
                people={people}
                value={taskAssigneeKey(task)}
                onChange={(key) => {
                    if (!key) onPatch({ clear_assignee: true });
                    else if (key.startsWith('user:')) onPatch({ assignee_uid: key.slice('user:'.length) });
                    else onPatch({ assignee_id: Number(key.slice('employee:'.length)) });
                }}
            />
            <Popover
                trigger="click"
                title="Note"
                content={(
                    <Input.TextArea
                        rows={3}
                        value={note}
                        style={{ width: 260 }}
                        onChange={(e) => setNote(e.target.value)}
                        onBlur={() => {
                            if (note !== (task.note ?? '')) onPatch({ note });
                        }}
                    />
                )}
            >
                <Badge count={task.note ? 1 : 0} size="small">
                    <Button type="text" icon={<CommentOutlined />} />
                </Badge>
            </Popover>
            <Popconfirm title="Delete this task?" onConfirm={onDelete}>
                <Button type="text" icon={<DeleteOutlined />} />
            </Popconfirm>
        </div>
    );
};

const TasksSection: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const campaignId = id ? Number(id) : undefined;
    const user = useAtomValue(userAtom);
    const { data: tasks = [], isLoading } = useCampaignTasks(campaignId);
    const assignees = useQuery<AssigneeOption[]>({
        queryKey: ['campaigns', 'assignees'],
        queryFn: async () => (await axiosApiInstance.get<AssigneeOption[]>('/api/campaigns/assignees')).data,
        staleTime: 5 * 60 * 1000,
    });
    const create = useCreateCampaignTask();
    const update = useUpdateCampaignTask();
    const remove = useDeleteCampaignTask();
    const [draft, setDraft] = React.useState('');
    const [due, setDue] = React.useState<dayjs.Dayjs | null>(null);
    const [selectedAssignee, setSelectedAssignee] = React.useState<string | undefined>();
    const [assigneeReady, setAssigneeReady] = React.useState(false);
    const [note, setNote] = React.useState('');
    const [scope, setScope] = React.useState<'all' | 'mine'>('mine');

    const roster = assignees.data ?? [];
    const self = roster.find((person) => person.email && user?.email && person.email.toLowerCase() === user.email.toLowerCase())
        ?? roster.find((person) => person.uid && person.uid === user?.uid);
    const selfKey = self ? assigneeKey(self) : undefined;
    React.useEffect(() => {
        if (assigneeReady || !selfKey) return;
        setSelectedAssignee(selfKey);
        setAssigneeReady(true);
    }, [selfKey, assigneeReady]);
    const mine = tasks.filter((t) => selfKey && taskAssigneeKey(t) === selfKey);
    const shown = scope === 'mine' ? mine : tasks;

    const add = async () => {
        const title = draft.trim();
        if (!campaignId || !title) return;
        try {
            const chosen = splitAssignee(selectedAssignee);
            await create.mutateAsync({
                campaignId,
                title,
                assigneeId: chosen.assigneeId,
                assigneeUid: chosen.assigneeUid,
                dueDate: due ? due.format('YYYY-MM-DD') : undefined,
                note: note.trim() || undefined,
            });
            setDraft('');
            setDue(null);
            setNote('');
            setSelectedAssignee(selfKey);
        } catch (e: any) {
            notification.error({ message: e?.response?.data?.error || 'Could not add the task' });
        }
    };

    if (isLoading) return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Segmented
                    value={scope}
                    onChange={(v) => setScope(v as 'all' | 'mine')}
                    options={[
                        { label: 'All', value: 'all' },
                        { label: `Mine${self ? ` (${mine.length})` : ''}`, value: 'mine' },
                    ]}
                />
                <Typography.Text type="secondary">{tasks.filter((t) => !t.done).length} open</Typography.Text>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                <Input
                    placeholder="Add a task"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onPressEnter={add}
                    style={{ flex: 1, minWidth: 180 }}
                />
                <DatePicker
                    value={due}
                    format="D MMM YYYY"
                    allowClear
                    placeholder="Due date"
                    suffixIcon={<CalendarOutlined />}
                    onChange={setDue}
                />
                <AssigneeSelect people={roster} value={selectedAssignee} onChange={setSelectedAssignee} />
                <Popover
                    trigger="click"
                    title="Note"
                    content={(
                        <Input.TextArea
                            rows={3}
                            value={note}
                            style={{ width: 260 }}
                            placeholder="Optional note"
                            onChange={(e) => setNote(e.target.value)}
                        />
                    )}
                >
                    <Badge count={note.trim() ? 1 : 0} size="small">
                        <Button icon={<CommentOutlined />} />
                    </Badge>
                </Popover>
                <Button type="primary" loading={create.isLoading} onClick={add}>Add</Button>
            </div>
            {scope === 'mine' && !self && (
                <Empty description="Your login is not an employee or a dev, so Mine has nothing to show." />
            )}
            {shown.length === 0 && (scope !== 'mine' || self) && (
                <Empty description={scope === 'mine' ? 'No tasks assigned to you' : 'No tasks yet'} />
            )}
            {groupTasks(shown).map((group) => (
                <div key={group.label} style={{ marginBottom: 16 }}>
                    <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{group.label}</Typography.Text>
                    {group.tasks.map((task) => (
                        <TaskRow
                            key={task.id}
                            task={task}
                            people={roster}
                            busy={update.isLoading}
                            onPatch={(patch) => {
                                if (!campaignId) return;
                                update.mutate(
                                    { campaignId, taskId: task.id, patch },
                                    { onError: (e: any) => notification.error({ message: e?.response?.data?.error || 'Could not update the task' }) },
                                );
                            }}
                            onDelete={() => campaignId && remove.mutate({ campaignId, taskId: task.id })}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
};

export default TasksSection;
